/**
 * AI Service for generating content via APIMart.
 */

// 1. SSRF Protection
const isSafeUrl = (urlString) => {
  try {
    const url = new URL(urlString);
    if (!['http:', 'https:'].includes(url.protocol)) return false;
    const hostname = url.hostname.toLowerCase();
    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1') return false;
    if (hostname.endsWith('.local') || hostname.endsWith('.internal')) return false;
    if (hostname.match(/^(10\.|172\.(1[6-9]|2[0-9]|3[0-1])\.|192\.168\.)/)) return false;
    return true;
  } catch (e) {
    return false;
  }
};

// 2. Helper to safely extract JSON from AI responses (handles markdown and text wrappers)
const parseAIResponse = (text) => {
  if (!text) throw new Error("Empty response from AI.");
  try {
    // Find the first '{' and last '}' to extract just the JSON object
    const start = text.indexOf('{');
    const end = text.lastIndexOf('}');
    if (start !== -1 && end !== -1) {
      const jsonStr = text.substring(start, end + 1);
      return JSON.parse(jsonStr);
    }
    return JSON.parse(text);
  } catch (e) {
    console.error("Failed to parse AI JSON:", text);
    throw new Error("AI returned malformed JSON. Please try again.");
  }
};

// 3. Fetch and extract text from URL with improved DOM parsing
const extractTextFromUrl = async (url) => {
  if (!isSafeUrl(url)) {
    throw new Error('SSRF Check Failed: Unsafe URL');
  }
  const proxyUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(url)}`;
  const response = await fetch(proxyUrl);
  if (!response.ok) throw new Error(`Proxy fetch failed: ${response.status}`);
  const data = await response.json();
  if (!data.contents) throw new Error('No content returned from proxy');

  const parser = new DOMParser();
  const doc = parser.parseFromString(data.contents, 'text/html');

  // Remove noise
  const noiseSelectors = ['script', 'style', 'noscript', 'nav', 'header', 'footer', 'aside', 'iframe', '.ad', '.ads', '.cookie-banner', '#comments'];
  noiseSelectors.forEach(sel => {
    doc.querySelectorAll(sel).forEach(el => el.remove());
  });

  // Try multiple logical containers
  let contentNode = doc.querySelector('article') || doc.querySelector('main') || doc.querySelector('[role="main"]') || doc.querySelector('#content') || doc.querySelector('.post-content') || doc.querySelector('.article-body') || doc.body;
  if (!contentNode) throw new Error("No readable content found on page.");

  const text = contentNode.textContent.replace(/\s+/g, ' ').trim();
  if (text.length < 200) {
    throw new Error(`Extracted text too short (${text.length} chars)`);
  }
  return text.substring(0, 25000);
};

// 4. Normal APIMart generation
export const generateNewsFromText = async (text, sourceHost) => {
  const apiKey = import.meta.env.VITE_API_MART_KEY;
  if (!apiKey) throw new Error('API_KEY_MISSING');

  // TRUNCATE TEXT to prevent Token Limit Exceeded errors from the AI
  const safeText = text.substring(0, 20000);
  const prompt = `You are the Pulse5 AI news editor. Create concise, factual, engaging AI news for fast mobile reading. Never invent facts. Return ONLY valid JSON.
Required JSON Structure:
{
  "headline": "string",
  "hook": "string (catchy, max 70 chars)",
  "summary": "string",
  "key_points": ["string 1", "string 2", "string 3"],
  "why_it_matters": "string",
  "category": "string",
  "cta_text": "string (Short Call to Action depending on content, e.g., 'Read full article', 'Try this tool', 'Get lifetime deal', or 'Copy this prompt')",
  "tags": ["tag1", "tag2"]
}`;

  const response = await fetch('https://api.apimart.ai/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`
    },
    body: JSON.stringify({
      model: "gpt-4o-mini", // Use a standard model name that proxies universally support
      messages: [
        { role: "system", content: prompt },
        { role: "user", content: `SOURCE ARTICLE CONTENT:\n\n${safeText}` }
      ],
      temperature: 0.3,
      max_tokens: 700,
      stream: false
    })
  });

  const data = await response.json();

  // Handle API-level errors returned as JSON (e.g., Rate limits, Invalid Model)
  if (!response.ok || data.error) {
    const errMsg = data.error?.message || data.error || response.statusText;
    throw new Error(`AI API Error: ${errMsg}`);
  }

  // Handle varied response structures
  const content = data.choices?.[0]?.message?.content || data.response || data.text || data.answer;
  if (!content) {
    console.error("Unexpected API Response Structure:", data);
    throw new Error("Invalid response structure from API Mart. Check console for details.");
  }

  const parsedContent = parseAIResponse(content);
  return {
    category: parsedContent.category || "AI News",
    hook: parsedContent.hook || parsedContent.headline || "New AI Update",
    summary: parsedContent.summary || "",
    points: (parsedContent.key_points || []).slice(0, 3),
    whyItMatters: parsedContent.why_it_matters || "",
    ctaText: parsedContent.cta_text || "",
    source: sourceHost,
    date: "Just now",
    readTime: "1 min"
  };
};

// 5. Fallback APIMart Web Search
const generateNewsFromWebSearchFallback = async (url, sourceHost) => {
  const apiKey = import.meta.env.VITE_API_MART_KEY;
  if (!apiKey) throw new Error('API_KEY_MISSING');

  const prompt = `Research this exact news/article URL and topic: ${url}. Find the original story and reliable supporting information. Return enough factual information to create a Pulse5 news summary. Do not invent facts. Return ONLY valid JSON with this structure:
{
  "headline": "string",
  "hook": "string (catchy, max 70 chars)",
  "summary": "string",
  "key_points": ["string 1", "string 2", "string 3"],
  "why_it_matters": "string",
  "category": "string",
  "cta_text": "string (Short Call to Action depending on content, e.g., 'Read full article', 'Try this tool', 'Get lifetime deal', or 'Copy this prompt')",
  "tags": ["tag1", "tag2"]
}`;

  const response = await fetch('https://api.apimart.ai/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`
    },
    body: JSON.stringify({
      model: "gpt-4o", // Upgraded model for the fallback
      messages: [
        { role: "system", content: prompt }
      ],
      temperature: 0.3,
      max_tokens: 700,
      stream: false
    })
  });

  const data = await response.json();

  if (!response.ok || data.error) {
    const errMsg = data.error?.message || data.error || response.statusText;
    throw new Error(`Fallback API Error: ${errMsg}`);
  }

  const content = data.choices?.[0]?.message?.content || data.response || data.text || data.answer;
  if (!content) {
    console.error("Unexpected API Response Structure:", data);
    throw new Error("Invalid response structure from API Mart Fallback.");
  }

  const parsedContent = parseAIResponse(content);
  return {
    category: parsedContent.category || "AI News",
    hook: parsedContent.hook || parsedContent.headline || "New AI Update",
    summary: parsedContent.summary || "",
    points: (parsedContent.key_points || []).slice(0, 3),
    whyItMatters: parsedContent.why_it_matters || "",
    ctaText: parsedContent.cta_text || "",
    source: sourceHost,
    date: "Just now",
    readTime: "1 min",
    usedFallback: true
  };
};

// 6. Main wrapper function with routing
export const generateNewsFromUrl = async (url, onProgress) => {
  const apiKey = import.meta.env.VITE_API_MART_KEY;
  if (!apiKey) {
    throw new Error('API Mart key is missing. Please add VITE_API_MART_KEY to your .env file.');
  }

  const sourceHost = isSafeUrl(url) ? new URL(url).hostname.replace('www.', '') : 'Unknown Source';

  if (onProgress) onProgress('Reading article...');

  try {
    // Step 1: Normal Extraction
    const articleText = await extractTextFromUrl(url);
    if (onProgress) onProgress('Creating Pulse5 summary...');
    const result = await generateNewsFromText(articleText, sourceHost);
    return { ...result, usedFallback: false };
  } catch (extractionError) {
    console.debug('[Extraction Fallback Log] DOMParser Failed:', extractionError.message);
    
    if (extractionError.message.includes('API_KEY_MISSING') || extractionError.message.includes('AI API Error')) {
      throw new Error(extractionError.message);
    }

    // Step 2: AI Web Search Fallback
    if (onProgress) onProgress('Direct reading unavailable — analyzing topic...');
    try {
      return await generateNewsFromWebSearchFallback(url, sourceHost);
    } catch (fallbackError) {
      console.debug('[Extraction Fallback Log] AI Fallback Failed:', fallbackError.message);
      if (fallbackError.message.includes('API_KEY_MISSING') || fallbackError.message.includes('Fallback API Error')) {
        throw new Error(fallbackError.message);
      }
      throw new Error('Could not automatically read or verify this article. Please paste the article text manually.');
    }
  }
};